import { isHlsUrl } from './utils.js';

/* Miniatura del momento al pasar por la barra de progreso.

   No hay miniaturas hechas de antemano: se abre el mismo vídeo una segunda vez, en pequeño y sin
   sonido, y se lleva al instante señalado. Eso pide al proveedor otra conexión mientras se
   muestra, así que solo se hace cuando la cuenta admite más de una, y la conexión se suelta a los
   pocos segundos de dejar la barra. En un televisor no se usa: muchos solo decodifican un vídeo
   a la vez y el segundo cortaría el que se está viendo. */

const IDLE_MS = 8000;
const SETTLE_MS = 120;

/**
 * ¿Se puede enseñar la vista previa de lo que se está viendo?
 * max: conexiones simultáneas que admite la cuenta (0 = no se sabe o sin límite).
 */
export function previewAllowed({ enabled = true, tv = false, live = false, url = '', local = false, max = 0 } = {}) {
    if (!enabled || tv || live || !url) return false;
    if (local) return true;
    if (isHlsUrl(url)) return false;
    return !(max > 0 && max < 2);
}

/** Instante al que se lleva la miniatura: se agrupa en pasos para no pedir un fotograma por píxel. */
export function previewTime(time, duration) {
    const d = Number(duration) || 0;
    const t = Math.max(0, Number(time) || 0);
    if (!d) return Math.round(t);
    const step = Math.max(2, Math.round(d / 300));
    return Math.min(Math.max(0, d - 1), Math.round(t / step) * step);
}

export class SeekPreview {
    /** box: el elemento donde va la miniatura. */
    constructor(box) {
        this.box = box;
        this.url = '';
        this.video = null;
        this.want = -1;
        this.shown = -1;
        this.seeking = false;
        this.failed = false;
        this._idle = null;
        this._settle = null;
    }

    /** Vídeo del que se sacan las miniaturas; '' para no enseñarlas. */
    setSource(url) {
        this.release();
        this.url = url || '';
        this.failed = false;
        this.want = -1;
        this.shown = -1;
        this.box.hidden = true;
        this.box.classList.remove('has-frame');
    }

    get available() {
        return !!this.url && !this.failed;
    }

    _attach() {
        if (this.video) return;
        const v = document.createElement('video');
        v.muted = true;
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.setAttribute('webkit-playsinline', '');
        v.preload = 'metadata';
        v.disablePictureInPicture = true;
        v.tabIndex = -1;
        v.addEventListener('loadedmetadata', () => this._pump());
        v.addEventListener('seeked', () => {
            this.seeking = false;
            this.shown = v.currentTime;
            this.box.classList.add('has-frame');
            this.box.classList.remove('is-seeking');
            this._pump();
        });
        v.addEventListener('error', () => {
            // Formato que el navegador no abre, o el proveedor rechaza la segunda conexión.
            this.failed = true;
            this.box.hidden = true;
            this.release();
        });
        this.video = v;
        this.box.appendChild(v);
        v.src = this.url;
    }

    /** Pide la miniatura del instante `time` (segundos). */
    request(time, duration) {
        if (!this.available) return;
        this.box.hidden = false;
        this.want = previewTime(time, duration);
        clearTimeout(this._idle);
        this._idle = setTimeout(() => this.release(), IDLE_MS);
        // Un momento de calma antes de pedir nada: al cruzar la barra de lado a lado no se pide.
        clearTimeout(this._settle);
        this._settle = setTimeout(() => {
            this._attach();
            this._pump();
        }, SETTLE_MS);
    }

    _pump() {
        const v = this.video;
        if (!v || this.seeking || this.want < 0 || v.readyState < 1) return;
        if (Math.abs(this.shown - this.want) < 1) return;
        this.seeking = true;
        this.box.classList.add('is-seeking');
        try {
            v.currentTime = this.want;
        } catch (e) {
            this.seeking = false;
        }
    }

    /** Suelta el vídeo (y con él la conexión con el proveedor). */
    release() {
        clearTimeout(this._idle);
        clearTimeout(this._settle);
        const v = this.video;
        this.video = null;
        this.seeking = false;
        this.shown = -1;
        this.box.classList.remove('has-frame', 'is-seeking');
        if (!v) return;
        try {
            v.removeAttribute('src');
            v.load();
        } catch (e) { /* ya descargado */ }
        if (v.parentNode) v.parentNode.removeChild(v);
    }
}
